import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAccount, useSwitchChain } from "wagmi";
import { useAuth } from "./index";
import { getDefaultChainId } from "app/utils/getDefaultChainId";
import { CHAIN_ID } from "app/utils/constants";
import {
  getSupportedNetworkChainIds,
  isSupportedNetworkChainId,
  normalizeChainId,
} from "app/utils/network-chain.utils";
import { NETWORK_SWITCH_ERROR_MESSAGES } from "./useNetworkManager.constants";

interface UseNetworkManagerProps {
  supportedNetworks: any[];
  autoSwitch?: boolean;
}

export const useNetworkManager = ({
  supportedNetworks,
  autoSwitch = true,
}: UseNetworkManagerProps) => {
  const { chainId: walletChainId } = useAccount();
  const { switchChainAsync } = useSwitchChain();
  const isAuth = useAuth();
  const pendingSwitchChainIdRef = useRef<number | null>(null);

  const [chainId, setChainId] = useState<number>(CHAIN_ID.LINEA_MAINNET);
  const [isNetworkSupported, setIsNetworkSupported] = useState(false);
  const [isSwitchingNetwork, setIsSwitchingNetwork] = useState(false);
  const [networkSwitchError, setNetworkSwitchError] = useState<string | null>(
    null
  );

  const supportedChainIds = useMemo(
    () => getSupportedNetworkChainIds(supportedNetworks),
    [supportedNetworks]
  );

  const checkNetworkSupport = useCallback(
    (currentChainId: unknown): boolean => {
      return isSupportedNetworkChainId(
        normalizeChainId(currentChainId),
        supportedChainIds
      );
    },
    [supportedChainIds]
  );

  const getOptimalChainId = useCallback((): number => {
    if (!supportedNetworks?.length) {
      console.warn(
        "NetworkManager: No supported networks provided, defaulting to LINEA_MAINNET"
      );
      return CHAIN_ID.LINEA_MAINNET;
    }

    if (
      isAuth &&
      walletChainId &&
      isSupportedNetworkChainId(
        normalizeChainId(walletChainId),
        supportedChainIds
      )
    ) {
      return walletChainId;
    }

    const defaultChainId =
      normalizeChainId(getDefaultChainId(supportedNetworks)) ||
      CHAIN_ID.LINEA_MAINNET;
    console.log(
      "NetworkManager: Using default chain ID:",
      defaultChainId,
      "from supported networks:",
      supportedChainIds
    );
    return defaultChainId;
  }, [isAuth, supportedChainIds, supportedNetworks, walletChainId]);

  const switchToChain = useCallback(
    async (targetChainId: unknown): Promise<boolean> => {
      const safeChainId = normalizeChainId(targetChainId);
      if (!safeChainId || !checkNetworkSupport(safeChainId)) {
        setNetworkSwitchError(NETWORK_SWITCH_ERROR_MESSAGES.unsupported);
        return false;
      }

      if (!switchChainAsync) {
        setNetworkSwitchError(NETWORK_SWITCH_ERROR_MESSAGES.unavailable);
        return false;
      }

      if (pendingSwitchChainIdRef.current === safeChainId) {
        setNetworkSwitchError(NETWORK_SWITCH_ERROR_MESSAGES.pending);
        return false;
      }

      pendingSwitchChainIdRef.current = safeChainId;
      setIsSwitchingNetwork(true);
      setNetworkSwitchError(null);

      try {
        await switchChainAsync({ chainId: safeChainId });
        return true;
      } catch (error) {
        setNetworkSwitchError(
          error instanceof Error ? error.message : String(error)
        );
        return false;
      } finally {
        if (pendingSwitchChainIdRef.current === safeChainId) {
          pendingSwitchChainIdRef.current = null;
        }
        setIsSwitchingNetwork(false);
      }
    },
    [checkNetworkSupport, switchChainAsync]
  );

  useEffect(() => {
    if (!supportedNetworks?.length) return;

    const optimalChainId = getOptimalChainId();
    const currentIsSupported = checkNetworkSupport(chainId);

    if (chainId !== optimalChainId || !currentIsSupported) {
      setChainId(optimalChainId);
      setIsNetworkSupported(checkNetworkSupport(optimalChainId));
    } else {
      setIsNetworkSupported(currentIsSupported);
    }

    if (autoSwitch && isAuth && walletChainId) {
      const isWalletChainSupported = checkNetworkSupport(walletChainId);
      if (!isWalletChainSupported) {
        console.log(
          "NetworkManager: wallet chain",
          walletChainId,
          "not supported, switching to",
          optimalChainId
        );
        void switchToChain(optimalChainId);
      }
    }
  }, [
    autoSwitch,
    chainId,
    checkNetworkSupport,
    getOptimalChainId,
    isAuth,
    supportedNetworks,
    switchToChain,
    walletChainId,
  ]);

  const handleNetworkChange = useCallback(
    (newChainId: number) => {
      const safeChainId = normalizeChainId(newChainId);
      if (!safeChainId || !checkNetworkSupport(safeChainId)) {
        setNetworkSwitchError(NETWORK_SWITCH_ERROR_MESSAGES.unsupported);
        return;
      }

      setChainId(safeChainId);
      setIsNetworkSupported(true);

      if (isAuth) {
        void switchToChain(safeChainId);
      }
    },
    [checkNetworkSupport, isAuth, switchToChain]
  );

  return {
    chainId,
    isNetworkSupported,
    isSwitchingNetwork,
    networkSwitchError,
    handleNetworkChange,
    supportedNetworks,
  };
};
